import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { SignOutButton } from '@/components/auth/SignOutButton';
import { cn } from '@/utils/cn';
import {
  brandsApi,
  type BrandAccount,
  type BrandRecord,
  type LinkableBrandGroup,
} from '@/api/client';
import { ANALYSIS_ROUTE } from '@/components/analysis/reportAnalysisUi';
import { workspaceCampaignPath } from '@/api/workspaceScope';

const BRANDS_QUERY_KEY = ['brands'] as const;
const BRAND_LINKABLE_QUERY_KEY = ['brand-linkable-accounts'] as const;

const SYSTEM_LABELS: Record<string, string> = {
  amazon_ads: 'Amazon Ads',
  flipkart_ads: 'Flipkart Ads',
  amazon_seller: 'Amazon Seller',
  amazon_vendor: 'Amazon Vendor',
  flipkart_seller: 'Flipkart Seller',
};

function accountKey(system: string, externalId: string): string {
  return `${system}\n${externalId}`;
}

function systemLabel(system: string): string {
  return SYSTEM_LABELS[system] ?? system;
}

function accountSummary(accounts: BrandAccount[]): string {
  const counts = new Map<string, number>();
  for (const account of accounts) {
    counts.set(account.system, (counts.get(account.system) ?? 0) + 1);
  }
  if (counts.size === 0) return 'No accounts mapped';
  return [...counts.entries()]
    .map(([system, count]) => `${count} ${systemLabel(system)}`)
    .join(', ');
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
      return detail.message;
    }
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

export function BrandsPage() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rename, setRename] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const brandsQuery = useQuery({
    queryKey: BRANDS_QUERY_KEY,
    queryFn: async () => {
      const res = await brandsApi.list();
      if (!res.success || !res.data) throw new Error('Failed to load brands');
      return res.data.brands;
    },
  });

  const linkableQuery = useQuery({
    queryKey: BRAND_LINKABLE_QUERY_KEY,
    queryFn: async () => {
      const res = await brandsApi.linkableAccounts();
      if (!res.success || !res.data) throw new Error('Failed to load accounts');
      return res.data.groups;
    },
  });

  const brands = brandsQuery.data ?? [];
  const selected = brands.find((brand) => brand.id === selectedId) ?? null;
  const groups = linkableQuery.data ?? [];

  const linkableKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const group of groups) {
      for (const account of group.accounts) keys.add(accountKey(account.system, account.externalId));
    }
    return keys;
  }, [groups]);

  const selectedSignature = (selected?.accounts ?? [])
    .map((account) => accountKey(account.system, account.externalId))
    .sort()
    .join('|');

  const linkableSignature = useMemo(() => [...linkableKeys].sort().join('|'), [linkableKeys]);

  useEffect(() => {
    const brand = brands.find((item) => item.id === selectedId);
    if (!brand) return;
    setRename(brand.name);
    setChecked(
      new Set(
        brand.accounts
          .filter((account) => linkableKeys.has(accountKey(account.system, account.externalId)))
          .map((account) => accountKey(account.system, account.externalId))
      )
    );
    // Signatures keep a refetch of the same accounts from clearing unsaved checkboxes.
    // brands and linkableKeys are read from this render when a signature changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkableSignature, selectedId, selectedSignature]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: BRANDS_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: BRAND_LINKABLE_QUERY_KEY });
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await brandsApi.create(name);
      if (!res.success || !res.data) throw new Error('Could not create brand');
      return res.data.brand;
    },
    onSuccess: async (brand) => {
      setName('');
      setSelectedId(brand.id);
      showToast('Brand created', 'success');
      await invalidate();
    },
    onError: (error) => showToast(apiErrorMessage(error, 'Could not create brand'), 'error'),
  });

  const renameMutation = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('Select a brand');
      const res = await brandsApi.rename(selected.id, rename);
      if (!res.success || !res.data) throw new Error('Could not rename brand');
      return res.data.brand;
    },
    onSuccess: async () => {
      showToast('Brand renamed', 'success');
      await invalidate();
    },
    onError: (error) => showToast(apiErrorMessage(error, 'Could not rename brand'), 'error'),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('Select a brand');
      const accounts = [...checked].flatMap((key) => {
        const splitAt = key.indexOf('\n');
        if (splitAt <= 0) return [];
        const system = key.slice(0, splitAt);
        const externalId = key.slice(splitAt + 1);
        if (!linkableKeys.has(key)) return [];
        return [{ system, externalId }];
      });
      const res = await brandsApi.replaceAccounts(selected.id, accounts);
      if (!res.success || !res.data) throw new Error('Could not save mapping');
      return res.data.brand;
    },
    onSuccess: async () => {
      showToast('Account mapping saved', 'success');
      await invalidate();
    },
    onError: (error) => showToast(apiErrorMessage(error, 'Could not save mapping'), 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (brand: BrandRecord) => {
      const res = await brandsApi.remove(brand.id);
      if (!res.success) throw new Error('Could not delete brand');
      return brand.id;
    },
    onSuccess: async (brandId) => {
      if (selectedId === brandId) setSelectedId(null);
      showToast('Brand deleted', 'success');
      await invalidate();
    },
    onError: (error) => showToast(apiErrorMessage(error, 'Could not delete brand'), 'error'),
  });

  const toggle = (system: string, externalId: string, next: boolean) => {
    const key = accountKey(system, externalId);
    setChecked((current) => {
      const copy = new Set(current);
      if (next) copy.add(key);
      else copy.delete(key);
      return copy;
    });
  };

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      <header className="border-b border-border/80 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3.5 sm:px-6">
          <ProductBrand variant="light" showTagline={false} />
          <nav className="flex flex-wrap items-center justify-end gap-x-5 gap-y-2" aria-label="Account">
            <Link to="/accounts" className="text-sm font-medium text-foreground-muted hover:text-foreground">
              Advertising accounts
            </Link>
            <Link to="/connections" className="text-sm font-medium text-foreground-muted hover:text-foreground">
              Connections
            </Link>
            <Link to={ANALYSIS_ROUTE} className="text-sm font-medium text-foreground-muted hover:text-foreground">
              Analysis
            </Link>
            <SignOutButton withIcon />
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6">
        <div>
          <h1 className="font-display text-headline-lg font-semibold text-brand-ink">Brands</h1>
          <p className="mt-1 max-w-2xl text-body-md text-foreground-muted">
            Group any number of Amazon Ads, Flipkart Ads, Seller, and Vendor accounts under a brand.
            The combined dashboard uses the mapped ads accounts.
          </p>
        </div>

        <form
          className="flex flex-col gap-2 rounded-2xl border border-border bg-surface p-4 shadow-sm sm:flex-row sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            if (!name.trim() || createMutation.isPending) return;
            createMutation.mutate();
          }}
        >
          <label className="min-w-0 flex-1 text-sm font-medium text-foreground">
            New brand
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Brand name"
              className="mt-1"
              maxLength={200}
            />
          </label>
          <Button type="submit" disabled={!name.trim() || createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create brand'}
          </Button>
        </form>

        {brandsQuery.isLoading ? (
          <div className="flex items-center gap-2 text-foreground-muted">
            <Spinner />
            Loading brands…
          </div>
        ) : brandsQuery.isError ? (
          <p className="text-sm text-error-ink">Could not load brands.</p>
        ) : brands.length === 0 ? (
          <p className="rounded-2xl border border-border bg-surface px-4 py-8 text-center text-sm text-foreground-muted">
            No brands yet. Create one, then map the accounts that belong to it.
          </p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
            <ul className="space-y-2">
              {brands.map((brand) => {
                const active = brand.id === selected?.id;
                return (
                  <li key={brand.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(brand.id)}
                      className={cn(
                        'w-full rounded-xl border px-3 py-3 text-left shadow-sm',
                        active ? 'border-brand bg-brand/5' : 'border-border bg-surface hover:border-brand/40'
                      )}
                    >
                      <span className="block truncate font-semibold text-foreground">{brand.name}</span>
                      <span className="mt-0.5 block text-xs text-foreground-muted">
                        {accountSummary(brand.accounts)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {selected ? (
              <section className="rounded-2xl border border-border bg-surface p-4 shadow-sm sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <form
                    className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-end"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!rename.trim() || rename.trim() === selected.name || renameMutation.isPending) return;
                      renameMutation.mutate();
                    }}
                  >
                    <label className="min-w-0 flex-1 text-sm font-medium text-foreground">
                      Brand name
                      <Input
                        value={rename}
                        onChange={(event) => setRename(event.target.value)}
                        className="mt-1"
                        maxLength={200}
                      />
                    </label>
                    <Button
                      type="submit"
                      variant="secondary"
                      disabled={!rename.trim() || rename.trim() === selected.name || renameMutation.isPending}
                    >
                      Rename
                    </Button>
                  </form>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={workspaceCampaignPath({ platform: 'brand', accountId: selected.id }, 'dashboard')}
                      className="inline-flex h-9 items-center rounded-lg bg-brand-ink px-3 text-sm font-medium text-brand-foreground hover:bg-chrome-border"
                    >
                      Open dashboard
                    </Link>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={deleteMutation.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete ${selected.name}? Mapped accounts stay connected.`)) {
                          deleteMutation.mutate(selected);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                <div className="mt-5 space-y-5">
                  {linkableQuery.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-foreground-muted">
                      <Spinner />
                      Loading accounts…
                    </div>
                  ) : linkableQuery.isError ? (
                    <p className="text-sm text-error-ink">Could not load accounts you can map.</p>
                  ) : (
                    groups.map((group) => (
                      <AccountGroup
                        key={group.system}
                        group={group}
                        selectedBrandId={selected.id}
                        checked={checked}
                        disabled={saveMutation.isPending}
                        onToggle={toggle}
                      />
                    ))
                  )}
                </div>

                <div className="mt-5 flex justify-end">
                  <Button
                    type="button"
                    disabled={saveMutation.isPending || linkableQuery.isLoading}
                    onClick={() => saveMutation.mutate()}
                  >
                    {saveMutation.isPending ? 'Saving…' : 'Save mapping'}
                  </Button>
                </div>
              </section>
            ) : (
              <p className="rounded-2xl border border-dashed border-border bg-surface px-4 py-10 text-center text-sm text-foreground-muted">
                Select a brand to map accounts.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function AccountGroup({
  group,
  selectedBrandId,
  checked,
  disabled,
  onToggle,
}: {
  group: LinkableBrandGroup;
  selectedBrandId: string;
  checked: Set<string>;
  disabled: boolean;
  onToggle: (system: string, externalId: string, next: boolean) => void;
}) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-foreground">{group.label}</h2>
      {group.accounts.length === 0 ? (
        <p className="mt-1 text-xs text-foreground-muted">No connected accounts.</p>
      ) : (
        <ul className="mt-2 max-h-64 space-y-0.5 overflow-y-auto rounded-xl border border-border p-1">
          {group.accounts.map((account) => {
            const key = accountKey(account.system, account.externalId);
            const taken = Boolean(account.brandId && account.brandId !== selectedBrandId);
            const isChecked = checked.has(key);
            return (
              <li key={key}>
                <label
                  className={cn(
                    'flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm',
                    taken ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-page',
                    isChecked && !taken && 'bg-brand/5'
                  )}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={isChecked}
                    disabled={disabled || taken}
                    onChange={(event) => onToggle(account.system, account.externalId, event.target.checked)}
                  />
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-foreground">{account.label}</span>
                    {account.currency ? (
                      <span className="block truncate text-xs text-foreground-subtle">{account.currency}</span>
                    ) : null}
                    {taken ? (
                      <span className="mt-0.5 block text-xs text-foreground-muted">
                        Mapped to {account.brandName || 'another brand'}
                      </span>
                    ) : null}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
